import React from 'react';
import './Gift_matching_page.css';
import GGApi from '../../../utils/GGApi';
import GG_loader from '../../general_components/GG_loader/GG_loader';
import MasterButton from '../../general_components/MasterButton/MasterButton';
import Overlay_popup from '../../general_components/Overlay_popup/Overlay_popup';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import dates from '../../../utils/dates';
import gift_matching_2x from '../../../images/illustrations/gift_matching_2x.png';
import { isGgPlusActive, readEntitlements } from '../../../utils/membershipUrl.js';
import { logGgPlusGiftMatchingGateViewed } from '../../../utils/ggPlusAnalytics.js';

function formatEndDate(ymdOrIso) {
    if (!ymdOrIso) return '—';
    const s = String(ymdOrIso).trim();
    if (s.length >= 10) return dates.jsDate(s.slice(0, 10));
    return s;
}

const MULTIPLIER = 2; // fixed for MVP

const matchExpirationMinDate = new Date();
const matchExpirationMaxDate = (() => {
    const d = new Date();
    d.setFullYear(d.getFullYear() + 10);
    return d;
})();

function parseYMD(ymd) {
    if (!ymd || typeof ymd !== 'string') return null;
    const parts = ymd.trim().split('-').map(Number);
    if (parts.length !== 3 || parts.some(isNaN)) return null;
    const [y, m, d] = parts;
    const date = new Date(y, m - 1, d);
    return isNaN(date.getTime()) ? null : date;
}

function toYMD(date) {
    if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
    return format(date, 'yyyy-MM-dd');
}

function isSelfMatchingProgram(p) {
    return p.matcher_user_id != null && p.matcher_user_id === p.user_id;
}

function matcherStatusLabel(program) {
    // Keep invite semantics clear: pending means awaiting confirm; ended means expired.
    if (program.status === 'active') return 'Active';
    if (program.status === 'paused') return 'Paused';
    if (program.status === 'ended') return 'Expired';
    return 'Pending';
}

class Gift_matching_page extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            access: '',
            plan: null,
            programs: [],
            // Setup form
            monthly_cap_dollars: '',
            match_expiration_date: null,
            // Edit overlay
            edit_open: false,
            edit_cap_dollars: '',
            edit_expiration_date: null,
            // Invite matcher overlay
            invite_overlay_open: false,
            invite_name: '',
            invite_relationship: '',
            invite_email: '',
            invite_cap: '',
            invite_expiration_date: null,
            // Congratulatory overlay after setting up own matching
            setup_success_overlay_open: false,
            // Overlay after pausing matching
            pause_success_overlay_open: false,
            // Overlay after invite sent
            invite_success_overlay_open: false,
            invite_success_email: '',
            // Explain "Your matching boost" (setup + active card)
            boost_help_open: false,
        };
        this.turnOnMatching = this.turnOnMatching.bind(this);
        this.pauseMatching = this.pauseMatching.bind(this);
        this.openEdit = this.openEdit.bind(this);
        this.closeEdit = this.closeEdit.bind(this);
        this.saveEdit = this.saveEdit.bind(this);
        this.openInviteOverlay = this.openInviteOverlay.bind(this);
        this.closeInviteOverlay = this.closeInviteOverlay.bind(this);
        this.sendInvite = this.sendInvite.bind(this);
        this.closeSetupSuccessOverlay = this.closeSetupSuccessOverlay.bind(this);
        this.closePauseSuccessOverlay = this.closePauseSuccessOverlay.bind(this);
        this.closeInviteSuccessOverlay = this.closeInviteSuccessOverlay.bind(this);
        this.openBoostHelp = this.openBoostHelp.bind(this);
        this.closeBoostHelp = this.closeBoostHelp.bind(this);
    }

    openBoostHelp() {
        this.setState({ boost_help_open: true });
    }

    closeBoostHelp() {
        this.setState({ boost_help_open: false });
    }

    closeInviteSuccessOverlay() {
        this.setState({ invite_success_overlay_open: false, invite_success_email: '' });
    }

    closeSetupSuccessOverlay() {
        this.setState({ setup_success_overlay_open: false });
    }

    closePauseSuccessOverlay() {
        this.setState({ pause_success_overlay_open: false });
    }

    componentDidMount() {
        const planId = this.props.match.params.plan_id;

        // Gift matching is GG+ only — redirect manual URL entry to the plan page.
        GGApi.checkToken().then(() => {
            const ent = readEntitlements();
            if (!isGgPlusActive(ent)) {
                logGgPlusGiftMatchingGateViewed({
                    source: 'gift_matching_page',
                    plan_id: Number(planId),
                });
                this.props.history.replace(`/plan/${planId}`);
                return;
            }

            GGApi.get_plan_by_id(planId).then(response => {
                if (response.access === 'No-access') {
                    this.setState({ loading: false, access: 'No-access' });
                    return;
                }
                if (response.fetched_plan) {
                    const plan = response.fetched_plan;
                    const programs = plan.matching_programs || [];
                    this.setState({
                        loading: false,
                        plan,
                        programs,
                    });
                } else {
                    this.setState({ loading: false });
                }
            });
        });
    }

    turnOnMatching() {
        const capDollars = parseFloat(this.state.monthly_cap_dollars);
        if (isNaN(capDollars) || capDollars < 0) {
            alert('Please enter a valid monthly cap.');
            return;
        }
        if (capDollars > 100000) {
            alert('Monthly cap cannot exceed $100,000.');
            return;
        }
        const monthly_cap_cents = Math.round(capDollars * 100);
        const endDateStr = toYMD(this.state.match_expiration_date);
        const planId = this.props.match.params.plan_id;

        // If user has a paused/ended "own" program, resume it instead of creating a new one
        const resumableOwnProgram = this.state.programs.find(
            (p) => isSelfMatchingProgram(p) && (p.status === 'paused' || p.status === 'ended')
        );
        if (resumableOwnProgram) {
            const payload = { status: 'active', monthly_cap_cents };
            if (endDateStr) payload.end_date = endDateStr;
            this.setState({ loading: true });
            GGApi.update_matching(planId, resumableOwnProgram.id, payload)
                .then((res) => {
                    if (res.matching_programs) {
                        this.setState({
                            programs: res.matching_programs,
                            monthly_cap_dollars: '',
                            match_expiration_date: null,
                            loading: false,
                            setup_success_overlay_open: true,
                        });
                    } else {
                        GGApi.get_matching(planId).then((list) => {
                            this.setState({ programs: Array.isArray(list) ? list : [], loading: false, setup_success_overlay_open: true });
                        }).catch(() => this.setState({ loading: false }));
                    }
                })
                .catch(() => {
                    this.setState({ loading: false });
                    alert('Something went wrong. Please try again.');
                });
            return;
        }

        const payload = { multiplier: MULTIPLIER, monthly_cap_cents };
        if (endDateStr) payload.end_date = endDateStr;
        this.setState({ loading: true });
        GGApi.create_matching(planId, payload)
            .then((res) => {
                if (!res.ok && res.requires_gg_plus) {
                    this.setState({ loading: false });
                    this.props.history.replace(`/plan/${planId}`);
                    return;
                }
                if (res.matching_programs) {
                    this.setState({
                        programs: res.matching_programs,
                        monthly_cap_dollars: '',
                        match_expiration_date: null,
                        loading: false,
                        setup_success_overlay_open: true,
                    });
                } else if (res.id) {
                    GGApi.get_matching(planId).then((list) => {
                        this.setState({ programs: Array.isArray(list) ? list : [], loading: false, setup_success_overlay_open: true });
                    }).catch(() => this.setState({ loading: false }));
                } else {
                    this.setState({ loading: false });
                    alert(res.message || 'Something went wrong. Please try again.');
                }
            })
            .catch(() => {
                this.setState({ loading: false });
                alert('Something went wrong. Please try again.');
            });
    }

    pauseMatching() {
        const myProgram = this.state.programs.find((p) => isSelfMatchingProgram(p));
        if (!myProgram || myProgram.status !== 'active') return;
        this.setState({ loading: true });
        GGApi.update_matching(
            this.props.match.params.plan_id,
            myProgram.id,
            { status: 'paused' }
        )
            .then((res) => {
                if (res.matching_programs) {
                    this.setState({ programs: res.matching_programs, loading: false, pause_success_overlay_open: true });
                } else {
                    GGApi.get_matching(this.props.match.params.plan_id).then((list) => {
                        this.setState({ programs: Array.isArray(list) ? list : [], loading: false, pause_success_overlay_open: true });
                    }).catch(() => this.setState({ loading: false }));
                }
            })
            .catch(() => {
                this.setState({ loading: false });
                alert('Something went wrong. Please try again.');
            });
    }

    openEdit() {
        const myProgram = this.state.programs.find((p) => isSelfMatchingProgram(p));
        if (myProgram && myProgram.status === 'active') {
        this.setState({
            edit_open: true,
            edit_cap_dollars: String(myProgram.monthly_cap_cents / 100),
            edit_expiration_date: parseYMD(myProgram.end_date ? String(myProgram.end_date).slice(0, 10) : '') || null,
        });
        }
    }

    closeEdit() {
        this.setState({ edit_open: false, edit_cap_dollars: '', edit_expiration_date: null });
    }

    openInviteOverlay() {
        this.setState({ invite_overlay_open: true });
    }

    closeInviteOverlay() {
        this.setState({
            invite_overlay_open: false,
            invite_name: '',
            invite_relationship: '',
            invite_email: '',
            invite_cap: '',
            invite_expiration_date: null,
        });
    }

    sendInvite() {
        const name = String(this.state.invite_name || '').trim();
        const email = String(this.state.invite_email || '').trim();
        const capDollars = parseFloat(this.state.invite_cap);
        if (!name) {
            alert('Please enter the matcher\'s name.');
            return;
        }
        if (!email) {
            alert('Please enter the matcher\'s email.');
            return;
        }
        if (isNaN(capDollars) || capDollars < 0) {
            alert('Please enter a valid monthly match limit.');
            return;
        }
        if (capDollars > 100000) {
            alert('Monthly cap cannot exceed $100,000.');
            return;
        }
        const monthly_cap_cents = Math.round(capDollars * 100);
        const payload = {
            multiplier: MULTIPLIER,
            monthly_cap_cents,
            matcher_name: name,
            matcher_email: email,
        };
        if (this.state.invite_relationship && String(this.state.invite_relationship).trim()) {
            payload.matcher_relationship = String(this.state.invite_relationship).trim();
        }
        const inviteEndDateStr = toYMD(this.state.invite_expiration_date);
        if (inviteEndDateStr) payload.end_date = inviteEndDateStr;
        this.setState({ loading: true });
        const invitedEmail = email;
        GGApi.create_matching(this.props.match.params.plan_id, payload)
            .then((res) => {
                if (res.matching_programs) {
                    this.setState({
                        programs: res.matching_programs,
                        invite_overlay_open: false,
                        invite_name: '',
                        invite_relationship: '',
                        invite_email: '',
                        invite_cap: '',
                        invite_expiration_date: null,
                        loading: false,
                        invite_success_overlay_open: true,
                        invite_success_email: invitedEmail,
                    });
                } else if (res.id) {
                    GGApi.get_matching(this.props.match.params.plan_id).then((list) => {
                        this.setState({
                            programs: Array.isArray(list) ? list : [],
                            invite_overlay_open: false,
                            invite_name: '',
                            invite_relationship: '',
                            invite_email: '',
                            invite_cap: '',
                            invite_expiration_date: null,
                            loading: false,
                            invite_success_overlay_open: true,
                            invite_success_email: invitedEmail,
                        });
                    }).catch(() => this.setState({ loading: false }));
                } else {
                    this.setState({ loading: false });
                    alert(res.message || 'Something went wrong. Please try again.');
                }
            })
            .catch(() => {
                this.setState({ loading: false });
                alert('Something went wrong. Please try again.');
            });
    }

    saveEdit() {
        const myProgram = this.state.programs.find((p) => isSelfMatchingProgram(p));
        if (!myProgram || myProgram.status !== 'active') return;
        const capDollars = parseFloat(this.state.edit_cap_dollars);
        if (isNaN(capDollars) || capDollars < 0) {
            alert('Please enter a valid monthly cap.');
            return;
        }
        if (capDollars > 100000) {
            alert('Monthly cap cannot exceed $100,000.');
            return;
        }
        const monthly_cap_cents = Math.round(capDollars * 100);
        const payload = { monthly_cap_cents };
        const endDateStr = toYMD(this.state.edit_expiration_date);
        if (endDateStr) payload.end_date = endDateStr;
        this.setState({ loading: true });
        GGApi.update_matching(this.props.match.params.plan_id, myProgram.id, payload)
            .then((res) => {
                if (res.matching_programs) {
                    this.setState({
                        programs: res.matching_programs,
                        edit_open: false,
                        edit_cap_dollars: '',
                        loading: false,
                    });
                } else {
                    GGApi.get_matching(this.props.match.params.plan_id).then((list) => {
                        this.setState({ programs: Array.isArray(list) ? list : [], edit_open: false, loading: false });
                    }).catch(() => this.setState({ loading: false }));
                }
            })
            .catch(() => {
                this.setState({ loading: false });
                alert('Something went wrong. Please try again.');
            });
    }

    render() {
        const planId = this.props.match.params.plan_id;
        const { plan, programs, loading, access, edit_open } = this.state;
        const setupFormReady = String(this.state.monthly_cap_dollars || '').trim() !== '' && this.state.match_expiration_date != null;
        const inviteFormReady = String(this.state.invite_name || '').trim() !== '' && String(this.state.invite_email || '').trim() !== '' && String(this.state.invite_cap || '').trim() !== '';

        if (loading) {
            return (
                <div className="home_background gift_matching_page_wrapper">
                    <main>
                        <GG_loader />
                    </main>
                </div>
            );
        }

        if (access === 'No-access') {
            return (
                <div className="home_background gift_matching_page_wrapper">
                    <main>
                        <p>We're sorry, but this page is unavailable.</p>
                    </main>
                </div>
            );
        }

        const matchExpirationPlaceholder = `01/01/${new Date().getFullYear() + 2}`;
        const myProgram = programs.find((p) => isSelfMatchingProgram(p));
        const myProgramActive = myProgram && myProgram.status === 'active';
        const activePrograms = programs.filter((p) => p.status === 'active');
        const hasActive = activePrograms.length > 0;

        const agg = (key) => programs.reduce((s, p) => s + (Number(p[key]) || 0), 0);
        const totalCapCents = activePrograms.reduce((s, p) => s + (Number(p.monthly_cap_cents) || 0), 0);
        const totalMatchedThisMonth = agg('matched_this_month_cents');
        const totalRemainingThisMonth = activePrograms.reduce((s, p) => s + (Number(p.remaining_this_month_cents) || 0), 0);
        const totalMatchedGifts = agg('total_matched_gifts');
        const totalMatchedValueCents = agg('total_matched_value_cents');

        return (
            <div className="home_background gift_matching_page_wrapper">
                <main>
                    <div className="gift_matching_main_content">
                        
                        {/* Card 1: Your matching boost – user's stats only when active; otherwise setup */}
                        <div className="gift_matching_card card">
                            {/* Title + help: opens overlay explaining pooled 2× matching */}
                            <div className="gift_matching_title_row">
                                <h2 className="gift_matching_title">Your matching boost</h2>
                                <i
                                    onClick={this.openBoostHelp}
                                    className="material-icons-outlined gift_matching_boost_help_icon"
                                    title="How does your matching boost work?"
                                    role="button"
                                    aria-label="How does your matching boost work?"
                                >help</i>
                            </div>

                            {myProgramActive ? (
                                <>
                                    <div className="gift_matching_stats_block">
                                        <p className="small_text"><span className="gift_matching_label">Match rate:</span> <span className="green_text">2x</span></p>
                                        <p className="small_text"><span className="gift_matching_label">Monthly match limit:</span> <span className="gift_matching_value">${(myProgram.monthly_cap_cents / 100).toFixed(0)}/month</span></p>
                                        <p className="small_text"><span className="gift_matching_label">Matching until:</span> <span className="gift_matching_value">{formatEndDate(myProgram.end_date)}</span></p>
                                    </div>
                                    <p className="align_center" style={{ marginTop: 12 }}>
                                        <span className="green_text cursor" onClick={this.openEdit}>Edit</span>
                                    </p>
                                </>
                            ) : (
                                <form>
                                    <p className="form_label">Monthly match limit</p>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100000"
                                        step="1"
                                        placeholder="$500"
                                        value={this.state.monthly_cap_dollars}
                                        onChange={(e) => this.setState({ monthly_cap_dollars: e.target.value })}
                                    />
                                    <p className="form_label">Match expiration</p>
                                    <DatePicker
                                        selected={this.state.match_expiration_date}
                                        onChange={(date) => this.setState({ match_expiration_date: date || null })}
                                        minDate={matchExpirationMinDate}
                                        maxDate={matchExpirationMaxDate}
                                        placeholderText={matchExpirationPlaceholder}
                                        dateFormat="MM/dd/yyyy"
                                        showIcon
                                        toggleCalendarOnIconClick
                                        className="gift_matching_date_input"
                                    />
                                    <div className="gift_matching_actions">
                                        <MasterButton
                                            buttonText="START GIFT MATCHING"
                                            inactive_button={!setupFormReady}
                                            disabled={!setupFormReady}
                                            handleButtonClick={this.turnOnMatching}
                                            notSticky={true}
                                        />
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* Card 2: Invite other matchers / Invited Matchers (only "other" matchers, not You) */}
                        <div className="gift_matching_card card gift_matching_invite_card">
                            {(() => {
                                const otherMatchers = programs.filter((p) => p.matcher_email != null && String(p.matcher_email).trim() !== '');
                                if (otherMatchers.length === 0) {
                                    return (
                                        <>
                                            <img src={gift_matching_2x} alt="" className="gift_matching_hero_img" />
                                            <h2 className="gift_matching_title">Invite other matchers</h2>
                                            <p className="gift_matching_intro">
                                                Invite family, friends, or organizations to help match gifts and grow savings faster.
                                            </p>
                                            <span className="green_text align_center cursor gift_matching_invite_cta" onClick={this.openInviteOverlay}>
                                                Invite a Matcher
                                            </span>
                                        </>
                                    );
                                }
                                return (
                                    <>
                                        <h2 className="gift_matching_title">Invited Matchers</h2>
                                        <ul className="gift_matching_matchers_list">
                                            {otherMatchers.map((p) => (
                                                <li key={p.id} className="gift_matching_matcher_row">
                                                    <span className="small_text">{p.matcher_name || p.matcher_email || 'Matcher'}</span>
                                                    <span className="small_text gift_matching_matcher_status">{matcherStatusLabel(p)}</span>
                                                </li>
                                            ))}
                                        </ul>
                                        <div className="gift_matching_invite_row cursor" onClick={this.openInviteOverlay}>
                                            <span className="small_text">Set up a new matcher</span>
                                            <i className="material-icons small_text">arrow_forward_ios</i>
                                        </div>
                                    </>
                                );
                            })()}
                        </div>

                        {/* Card 3: Matching Stats – aggregate; full when active, paused version when not */}
                        {programs.length > 0 && (
                            <div className="gift_matching_card card">
                                <h2 className="gift_matching_title">Matching Stats</h2>
                                {hasActive ? (
                                    <div className="gift_matching_stats_block">
                                        <p className="small_text"><span className="gift_matching_label">Match rate:</span> <span className="green_text">2x</span></p>
                                        <p className="small_text"><span className="gift_matching_label">Monthly match limit:</span> <span className="gift_matching_value">${(totalCapCents / 100).toFixed(0)}/month</span></p>
                                        <div className="gift_matching_stats_separator" />
                                        <p className="small_text"><span className="gift_matching_label">Matching this month:</span> <span className="gift_matching_value">${(totalMatchedThisMonth / 100).toFixed(0)}</span></p>
                                        <p className="small_text"><span className="gift_matching_label">Remaining this month:</span> <span className="gift_matching_value">${(totalRemainingThisMonth / 100).toFixed(0)}</span></p>
                                        <div className="gift_matching_stats_separator" />
                                        {/*
                                        <p className="small_text"><span className="gift_matching_label">Total matched gifts:</span> <span className="gift_matching_value">{totalMatchedGifts}</span></p>
                                        */}
                                        <p className="small_text"><span className="gift_matching_label">Total matched gifts value:</span> <span className="gift_matching_value">${(totalMatchedValueCents / 100).toFixed(0)}</span></p>
                                    </div>
                                ) : (
                                    <>
                                        <p className="gift_matching_status">Gift matching is paused!</p>
                                        <div className="gift_matching_stats_block">
                                            <p className="small_text"><span className="gift_matching_label">Matched Gifts:</span> <span className="gift_matching_value">{totalMatchedGifts}</span></p>
                                            <p className="small_text"><span className="gift_matching_label">Matched Gifts Value:</span> <span className="gift_matching_value">${(totalMatchedValueCents / 100).toFixed(0)}</span></p>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}

                        {/* Bottom button */}
                        <div className="gift_matching_bottom_button">
                            <MasterButton
                                buttonText={programs.length === 0 ? 'BACK TO PLAN' : 'DONE'}
                                notSticky={true}
                                links={true}
                                linkTo={`/plan/${planId}`}
                            />
                        </div>

                        <div className="buffer" />
                    </div>

                    {/* Your matching boost – short explainer */}
                    <Overlay_popup
                        open={this.state.boost_help_open}
                        closer={true}
                        handleClose={this.closeBoostHelp}
                        overlay_title="Your matching boost"
                        body_1="You set a monthly match limit and an end date. While matching is on, eligible gifts to this plan are boosted at 2×, up to your limit and within any combined matchers’ caps for the month."
                        body_2="Each month you get a summary of what to match and can pay from that email. Pausing stops new matches."
                        button_1={{
                            text: 'GOT IT',
                            handler: this.closeBoostHelp,
                        }}
                    />

                    {/* Edit overlay */}
                    <Overlay_popup
                        open={edit_open}
                        closer={true}
                        handleClose={this.closeEdit}
                        overlay_title="Manage Matcher"
                        form={
                            <form>
                                <p className="form_label">Monthly match limit</p>
                                <input
                                    type="number"
                                    min="0"
                                    max="100000"
                                    step="1"
                                    placeholder="$500"
                                    value={this.state.edit_cap_dollars}
                                    onChange={(e) => this.setState({ edit_cap_dollars: e.target.value })}
                                />
                                <p className="form_label">Match expiration</p>
                                <DatePicker
                                    selected={this.state.edit_expiration_date}
                                    onChange={(date) => this.setState({ edit_expiration_date: date || null })}
                                    minDate={matchExpirationMinDate}
                                    maxDate={matchExpirationMaxDate}
                                    placeholderText={matchExpirationPlaceholder}
                                    dateFormat="MM/dd/yyyy"
                                    showIcon
                                    toggleCalendarOnIconClick
                                    className="gift_matching_date_input"
                                />
                            </form>
                        }
                        button_1={{
                            text: 'SAVE CHANGES',
                            handler: this.saveEdit,
                        }}
                        joker={
                            <div style={{ marginTop: 16, marginLeft: '5%', marginRight: '5%' }}>
                                <p className="small_text" style={{ marginBottom: 8 }}>Pausing stops new matches for this program. You can resume anytime.</p>
                                <MasterButton
                                    buttonText="PAUSE MATCHING"
                                    handleButtonClick={() => { this.closeEdit(); this.pauseMatching(); }}
                                    notSticky={true}
                                />
                            </div>
                        }
                    />

                    {/* Overlay after pausing matching */}
                    <Overlay_popup
                        open={this.state.pause_success_overlay_open}
                        closer={true}
                        handleClose={this.closePauseSuccessOverlay}
                        overlay_title="Matching paused"
                        body_1="Your gift matching has been paused. No new matches will be made while it's paused."
                        body_2="Any matches already accrued this month will still be included in your monthly summary. You can resume anytime by setting your monthly limit and expiration date, then clicking Start Gift Matching."
                        button_1={{
                            text: 'GOT IT',
                            handler: this.closePauseSuccessOverlay,
                        }}
                    />

                    {/* Success overlay after invite sent */}
                    <Overlay_popup
                        open={this.state.invite_success_overlay_open}
                        closer={true}
                        handleClose={this.closeInviteSuccessOverlay}
                        overlay_title="Invite sent"
                        body_1={this.state.invite_success_email
                            ? `We've sent an invite to ${this.state.invite_success_email}. They'll need to confirm to start matching.`
                            : "We've sent the invite. The matcher will need to confirm to start matching."}
                        button_1={{
                            text: 'GOT IT',
                            handler: this.closeInviteSuccessOverlay,
                        }}
                    />

                    {/* Congratulatory overlay after setting up own matching */}
                    <Overlay_popup
                        open={this.state.setup_success_overlay_open}
                        closer={true}
                        handleClose={this.closeSetupSuccessOverlay}
                        overlay_title="You're all set!"
                        body_1="Congratulations! Your gift matching is now active. Every gift sent to this plan will be matched at 2x up to your monthly limit."
                        joker={ 
                            <div style={{ marginTop: 16, marginLeft: '5%', marginRight: '5%' }}>
                            <p><br></br>Want to grow savings even faster?<br></br> <br></br> 
                        Invite family or friends to match gifts too!</p>
                        <MasterButton
                                    buttonText="GOT IT"
                                    handleButtonClick={() => { this.closeSetupSuccessOverlay()}}
                                    notSticky={true}
                                />
                            </div>
                        }
                    />

                    {/* Invite matcher overlay */}
                    <Overlay_popup
                        open={this.state.invite_overlay_open}
                        closer={true}
                        handleClose={this.closeInviteOverlay}
                        overlay_title="Invite Matcher"
                        form={
                            <form>
                                <p className="form_label">Matcher Name:</p>
                                <input type="text" placeholder="New name" value={this.state.invite_name || ''} onChange={(e) => this.setState({ invite_name: e.target.value })} />
                                <p className="form_label">Relationship to Gift Receiver</p>
                                <input type="text" placeholder="Grandmother / Godfather / Auntie..." value={this.state.invite_relationship || ''} onChange={(e) => this.setState({ invite_relationship: e.target.value })} />
                                <p className="form_label">Email:</p>
                                <input type="email" placeholder="Email" value={this.state.invite_email || ''} onChange={(e) => this.setState({ invite_email: e.target.value })} />
                                <p className="form_label">Monthly match limit</p>
                                <input type="number" min="0" max="100000" placeholder="$500" value={this.state.invite_cap || ''} onChange={(e) => this.setState({ invite_cap: e.target.value })} />
                                <p className="form_label">Match expiration</p>
                                <DatePicker
                                    selected={this.state.invite_expiration_date}
                                    onChange={(date) => this.setState({ invite_expiration_date: date || null })}
                                    minDate={matchExpirationMinDate}
                                    maxDate={matchExpirationMaxDate}
                                    placeholderText={matchExpirationPlaceholder}
                                    dateFormat="MM/dd/yyyy"
                                    showIcon
                                    toggleCalendarOnIconClick
                                    className="gift_matching_date_input"
                                />
                                <p className="form_label" style={{ marginTop: 16 }}>How this works:</p>
                                <p className="small_text">Matchers are invited via email and confirm the matching terms you set. After they confirm they’ll match, they’ll get a monthly email with a summary of gifts they're matching and a link to confirm payment.</p>
                            </form>
                        }
                        button_1={{
                            text: 'SEND INVITE',
                            handler: this.sendInvite,
                            inactive_button: !inviteFormReady,
                            disabled: !inviteFormReady,
                        }}
                    />
                </main>
            </div>
        );
    }
}

export default Gift_matching_page;
